/**
 * Whether the person typed something that a reload would throw away (F-61).
 *
 * There is no form library here and no shared "dirty" flag, so this watches
 * the document instead: every field the person types into is remembered until
 * its form is submitted or it leaves the page (a dialog that saved and
 * closed). Asked before the update notice reloads.
 *
 * It leans towards asking. A field that stays on screen after a save that is
 * not a form submit still counts, and the cost of that is one extra question;
 * the cost of the opposite is somebody's text.
 */

const touched = new Set<HTMLElement>()
let installed = false

/** Typing that is not content: searching and filtering lose nothing on reload. */
const isIgnored = (element: HTMLElement): boolean => {
  if (element.closest('[data-unsaved-ignore]')) return true
  if (element instanceof HTMLInputElement) {
    const type = element.type
    if (type === 'search' || type === 'password' || type === 'checkbox' || type === 'radio' || type === 'file') return true
    if (element.getAttribute('role') === 'searchbox' || element.getAttribute('role') === 'combobox') return true
  }
  return false
}

const hasContent = (element: HTMLElement): boolean => {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    return element.value.trim() !== ''
  }
  return (element.textContent || '').trim() !== ''
}

export const installUnsavedChangesTracker = (): void => {
  if (installed || typeof document === 'undefined') return
  installed = true

  document.addEventListener(
    'input',
    (event) => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      /* A rich-text editor fires from inside its contenteditable root. */
      const field = target.isContentEditable
        ? ((target.closest('[contenteditable="true"]') as HTMLElement | null) ?? target)
        : target
      if (!isIgnored(field)) touched.add(field)
    },
    true
  )

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return
      for (const field of Array.from(touched)) {
        if (form.contains(field)) touched.delete(field)
      }
    },
    true
  )
}

export const hasUnsavedChanges = (): boolean => {
  for (const field of Array.from(touched)) {
    if (!field.isConnected) touched.delete(field)
  }
  return Array.from(touched).some(hasContent)
}
